<template>
  <div :class="['k-select group', {
        'disabled': disabled,
        'opened': dropdownOpened,
        'inline': inlineDropdown
    }]" ref="dropdownWrapper">

    <div class="dropdown no-arrow">
      <div
          id="dropdownMenuButton"
          ref="dropdownToggle"
          :class="['input btn btn-outline dropdown-toggle', {'simplified': simplified, 'error': localError }]"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          @click="handleDropdownClick"
          v-bind:data-bs-auto-close="autoClose">

        <label v-if="label.length">{{ label }} <span v-if="required">*</span></label>

        <div class="d-flex align-items-center justify-content-between w-100">
          <div class="d-flex w-100 gap-2">
            <slot name="labelLeftIcon"></slot>
            <div v-if="localSelected">
              <div v-if="multiple">
                <slot name="selectedItems" v-bind="{options: localSelected, showTags}">
                  <div v-if="showTags && localSelected?.length" class="d-flex gap-1 pt-1">
                    <div v-for="n in Math.min(showTags, localSelected.length)" :key="n">
                      <div class="badge bg-black">
                        {{ localSelected[n - 1]?.label }}
                      </div>
                    </div>
                    <div v-if="localSelected.length > showTags" class="badge bg-white text-dark border">
                      +{{ localSelected.length - showTags }}
                    </div>
                  </div>
                  <div v-else>
                    {{ localSelected?.length }} {{ selectedText }}
                  </div>
                </slot>
              </div>
              <div v-else>
                <span v-if="localSelected?.length">
                   <slot name="selectedItem" v-bind="{option: localSelected[0]}">
                    {{ localSelected[0].label }}
                   </slot>
                </span>
                <span v-else class="placeholder-text">
                    {{ placeholder }}
                </span>
              </div>
            </div>
          </div>
          <slot name="labelRightIcon"></slot>
        </div>

        <i class="fi fi-rr-angle-small-down caret"></i>
      </div>
      <div :class="['dropdown-menu dropdown-menu-end']" ref="dropdownMenu" aria-labelledby="dropdownMenuButton">
        <div v-if="allowSearch" class="group form-group has-left-icon has-right-icon position-relative">
          <div class="left-icon">
            <i :class="['fi', searchIcon]"></i>
          </div>

          <input
              ref="searchInput"
              type="text"
              class="form-control search-input"
              v-model="search"
              :placeholder="searchPlaceholder"
              @keydown.enter="handleSearchEnter">

          <div v-if="searchHelp.length && !searching && !search.length" class="right-icon"
               :title="searchHelp" data-bs-toggle="tooltip">
            <i class="fi fi-rr-interrogation"></i>
          </div>

          <div v-if="!searching && search.length" class="right-icon" @click="clearSearchInput" role="button">
            <i class="fi fi-rr-cross-small"></i>
          </div>

          <div v-if="searching" class="right-icon spinner-border" role="status"></div>
        </div>

        <slot name="header"></slot>

        <div :class="['selected-options bg-light-green', {'pe-none' : disableSelecting}]"
             v-if="showSelectedOptions">
          <div @click="localCollapseSelectedOptions = !localCollapseSelectedOptions"
               class="d-flex justify-content-between align-items-center py-2 px-3" role="button">
            <div class="title small">{{ trans('general.defaults.selected_items') }} ({{ localSelected.length }})
            </div>
            <i :class="['fi', localCollapseSelectedOptions ? 'fi-rr-angle-small-up' : 'fi-rr-angle-small-down']"></i>
          </div>
          <div :class="['items scrollable', {'pb-2' : localCollapseSelectedOptions}]"
               :style="{'max-height': scrollMaxHeight ? scrollMaxHeight + 'px' : ''}">
            <p
                v-if="localCollapseSelectedOptions"
                v-for="option in localSelected"
                :key="option.value"
                v-bind:data-option-id="option.id"
                class="selected-item item py-1 px-3 mb-0 w-100" role="button"
                @click="toggleOption">
              <slot name="selectedItem" v-bind:option="option">
                <span>{{ option.label }}</span>
              </slot>
            </p>
            <p v-if="localCollapseSelectedOptions && !localSelected.length" class="text-center py-2">
              {{ trans('general.defaults.no_item_selected') }}
            </p>
          </div>
        </div>

        <div :class="['items scrollable', {'pe-none' : disableSelecting}]"
             v-if="showResults"
             :style="{'max-height': scrollMaxHeight ? scrollMaxHeight + 'px' : ''}">
          <div
              v-for="option in filteredOptions"
              :key="option.value"
              v-bind:data-option-id="option.id"
              :class="['dropdown-item', { selected: isOptionSelected(option) }]"
              @click="toggleOption">
            <slot name="resultItem" v-bind:option="option">
              <span>{{ option.label }}</span>
            </slot>
          </div>
          <div v-if="!filteredOptions || filteredOptions.length === 0"
               class="py-2 px-2 text-center">
            <slot name="noResults" v-bind:search="search">
              <label class="large">{{ trans('general.defaults.no_results_found') }}</label>
            </slot>
          </div>
        </div>
      </div>
    </div>
    <div class="invalid-feedback d-block" v-if="localError">
      {{ localError }}
    </div>
  </div>
</template>

<script>
import {Dropdown} from "bootstrap";

export default {
  name: 'KSelect',
  props: {
    value: {
      type: [Array, Object],
      default: () => [],
      required: false,
    },
    preSelected: {
      type: [Array, Object],
      required: false,
    },
    options: {
      type: Array,
      required: true,
    },
    error: {
      type: String,
      required: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    selectedText: {
      type: String,
      default: trans('general.defaults.selected'),
    },
    placeholder: {
      type: String,
      default: trans('general.defaults.select_items'),
    },
    showTags: {
      type: Number,
      default: 0,
    },
    label: {
      type: String,
      default: '',
    },
    autoClose: {
      type: String,
      default: 'false',
    },
    simplified: {
      type: Boolean,
      default: false,
    },
    showResults: {
      type: Boolean,
      default: true,
    },
    allowSearch: {
      type: Boolean,
      default: true,
    },
    searchPlaceholder: {
      type: String,
      default: trans('general.defaults.search'),
    },
    searchIcon: {
      type: String,
      default: 'fi-rr-search',
    },
    serverSideSearch: {
      type: Boolean,
      default: false,
    },
    searchHelp: {
      type: String,
      default: '',
    },
    showSelectedOptions: {
      type: Boolean,
      default: true,
    },
    collapseSelectedOptions: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    disableSelecting: {
      type: Boolean,
      default: false,
    },
    inlineDropdown: {
      type: Boolean,
      default: false,
    },
    scrollMaxHeight: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      localSelected: this.preSelected ?? this.value,
      multipleSelected: [],
      search: '',
      searchDebounce: '',
      searching: false,
      filteredOptions: this.options,
      debounceTimeout: null,
      localCollapseSelectedOptions: this.collapseSelectedOptions,
      localError: this.error,
      dropdownOpened: false,
    }
  },
  watch: {
    value(newVal) {
      this.localSelected = newVal;
    },
    options() {
      this.filterOptions(this.searchDebounce);
    },
    search(value) {
      this.debounceSearch(value);
    },
    error: function (val) {
      this.localError = val
    },
  },
  methods: {
    handleDropdownClick() {
      if (this.$refs.searchInput) {
        this.dropdownOpened ? this.$refs.searchInput.focus() : this.$refs.searchInput.blur();
      }
    },
    clearSearchInput(e) {
      e.stopPropagation();
      this.search = '';
    },
    debounceSearch(query) {
      this.searching = true;
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => {
        this.searchDebounce = query;
        if (this.serverSideSearch) {
          this.$emit('search', query);
        } else {
          this.filterOptions(query);
        }
      }, 300);
    },
    filterOptions(query) {
      if (query) {
        this.filteredOptions = this.options.filter((option) =>
            option.label.toLowerCase().includes(query.toLowerCase())
        );
      } else {
        this.filteredOptions = this.options;
      }
      this.searching = false;
    },
    onSelectChange() {
      this.$emit('input', this.localSelected);
      this.$emit('change', this.localSelected);
      this.$emit('reset-error');
    },
    isOptionSelected(option) {
      return this.localSelected && this.localSelected.some(selectedOption => selectedOption.id === option.id);
    },
    toggleOption(e) {
      let target = e.target;
      if (!e.target.getAttribute('data-option-id')) {
        target = e.target.closest('[data-option-id]');
      }
      const optionId = target.getAttribute('data-option-id');
      const option = this.options.find(option => option.id?.toString() === optionId);

      if (!this.localSelected) {
        this.localSelected = [];
      }

      const optionIndex = this.localSelected.findIndex(item => item.id?.toString() === optionId);
      if (this.multiple) {
        if (optionIndex > -1) {
          if (this.required && this.localSelected.length <= 1) {
            this.$notify({
              type: 'warn',
              title: trans('general.defaults.at_least_one_item_required'),
            });
            return;
          }
          this.localSelected = this.localSelected.filter(item => item.id?.toString() !== optionId);
        } else {
          this.localSelected = [...this.localSelected, option];
        }

      } else {
        if (optionIndex > -1) {
          if (!this.required) {
            this.localSelected = [];
          }
        } else {
          this.localSelected = [option];
        }
      }

      this.onSelectChange();
    },
    handleClickOutside(event) {
      const dropdownWrapper = this.$refs.dropdownWrapper;

      if (dropdownWrapper && !dropdownWrapper.contains(event.target)) {
        this.dropdownOpened = false;
        const dropdownToggle = this.$refs.dropdownToggle;
        const dropdown = Dropdown.getInstance(dropdownToggle) || new Dropdown(dropdownToggle);
        dropdown.hide();
      }
    },
    handleSearchEnter() {
      this.$emit('search-enter', this.search);
    },
    setSearchQuery(val) {
      this.search = val;
    },
    select(val) {
      this.localSelected = val;
      this.$emit('change', val);
    },
    initTooltips() {
      setTimeout(() => {
        const tooltipTriggerList = [].slice.call(document.querySelectorAll('.k-select [data-bs-toggle="tooltip"]'));
        tooltipTriggerList.map(function (tooltipTriggerEl) {
          return new bootstrap.Tooltip(tooltipTriggerEl)
        });
      }, 700);
    },
    onDropdownShown() {
      this.dropdownOpened = true;
    },
    onDropdownClosed() {
      this.dropdownOpened = false;
    }
  },
  mounted() {
    this.initTooltips();
    this.$nextTick(() => {
      document.addEventListener('click', this.handleClickOutside, true);
    });
    this.$refs.dropdownToggle.addEventListener('shown.bs.dropdown', this.onDropdownShown);
    this.$refs.dropdownToggle.addEventListener('hidden.bs.dropdown', this.onDropdownClosed);
    this.$emit('mounted');
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside, true);
    this.$refs.dropdownToggle.removeEventListener('shown.bs.dropdown', this.onDropdownShown);
    this.$refs.dropdownToggle.removeEventListener('hidden.bs.dropdown', this.onDropdownClosed);
  },
}
</script>
